import {A} from "@solidjs/router";
import Container from "~/components/Container";
import Page from "~/components/Page";
import Section from "~/components/Section";

export default function Privacy() {
    return (
        <Page metaTitle="Privacy Policy"
              metaDescription="Discover how Graceful Space Yoga values and protects your privacy. Learn how we collect, use, and safeguard your personal information when you book and attend our yoga classes, and understand your privacy rights.">
            <Container>
                <Section>
                    <h1>Privacy Policy</h1>
                    <p>Last Updated: October 13, 2025</p>
                </Section>

                <Section id="intro">
                    <h2>Introduction</h2>
                    <p>
                        This Privacy Policy explains how Graceful Space Yoga ("we", "us", "our") collects, uses,
                        discloses,
                        and safeguards your personal information when you use our website gracefulspaceyoga.com (the
                        "Site")
                        and our services, including creating an account, purchasing class passes, and booking/attending
                        in‑person yoga classes (the "Services"). It also explains your rights and choices.
                    </p>
                    <p>
                        Please read this Privacy Policy together with our <A href="/terms">Terms & Conditions</A>.
                    </p>
                </Section>

                <Section>
                    <h2>Table of Contents</h2>
                    <ol>
                        <li><A href="#who">Who We Are</A></li>
                        <li><A href="#data-we-collect">Information We Collect</A></li>
                        <li><A href="#how-we-use">How We Use Your Information</A></li>
                        <li><A href="#legal-bases">Legal Bases (UK/EU)</A></li>
                        <li><A href="#cookies">Cookies and Similar Technologies</A></li>
                        <li><A href="#analytics">Analytics</A></li>
                        <li><A href="#payments">Payments</A></li>
                        <li><A href="#sharing">Disclosure of Information</A></li>
                        <li><A href="#international">International Transfers</A></li>
                        <li><A href="#retention">Data Retention</A></li>
                        <li><A href="#security">Security</A></li>
                        <li><A href="#your-rights">Your Rights</A></li>
                        <li><A href="#children">Children</A></li>
                        <li><A href="#changes">Changes to this Policy</A></li>
                        <li><A href="#contact">Contact</A></li>
                    </ol>
                </Section>

                <Section id="who">
                    <h2>1. Who We Are</h2>
                    <p>
                        Graceful Space Yoga provides Vinyasa flow yoga classes and an online booking system. We operate
                        in the UK.
                        References to "we", "us" or "our" are to Graceful Space Yoga.
                    </p>
                </Section>

                <Section id="data-we-collect">
                    <h2>2. Information We Collect</h2>
                    <p>
                        The types of information we collect depend on how you use the Site and Services:
                    </p>
                    <ul>
                        <li>
                            Account Information: name, email address, password (hashed), and optional profile details.
                        </li>
                        <li>
                            Booking & Purchase Information: class selections, class pass purchases, Stripe checkout
                            session
                            identifiers, payment status and records associated with your bookings.
                        </li>
                        <li>
                            Health Information (optional): during registration you may provide information about health
                            conditions relevant to practicing yoga. This is special category data and is entirely
                            optional.
                        </li>
                        <li>
                            Communications: messages you send to us and related contact details.
                        </li>
                        <li>
                            Technical & Usage Data: IP address, browser type, device information, pages viewed, and
                            interactions with the Site. This may be collected via logs and analytics.
                        </li>
                        <li>
                            Cookies & Similar Technologies: see the Cookies section below.
                        </li>
                    </ul>
                </Section>

                <Section id="how-we-use">
                    <h2>3. How We Use Your Information</h2>
                    <ul>
                        <li>Provide, operate, and maintain the Site and Services.</li>
                        <li>Create and manage user accounts.</li>
                        <li>Process purchases of class passes and manage class bookings and attendance.</li>
                        <li>Communicate with you about your bookings, passes, account updates, and service notices.</li>
                        <li>Improve the Site and Services, including safety, performance, and user experience.</li>
                        <li>Comply with legal obligations and enforce our <A href="/terms">Terms</A>.</li>
                        <li>With your explicit consent, consider any health information you provide to support safe
                            participation in classes.
                        </li>
                    </ul>
                </Section>

                <Section id="legal-bases">
                    <h2>4. Legal Bases (UK/EU)</h2>
                    <ul>
                        <li>Contract: to create your account, process payments, and provide booked classes.</li>
                        <li>Legitimate Interests: to secure our Site, prevent misuse, and improve our Services.</li>
                        <li>Legal Obligation: to maintain records required by tax and accounting laws.</li>
                        <li>
                            Consent: for optional activities such as certain analytics cookies (where applicable),
                            marketing communications,
                            and processing of special category data (health information) you choose to provide.
                        </li>
                    </ul>
                </Section>

                <Section id="cookies">
                    <h2>5. Cookies and Similar Technologies</h2>
                    <p>
                        We use cookies and similar technologies to operate and secure the Site (e.g., session and
                        security cookies),
                        to remember your preferences, and to understand how the Site is used. You can usually control
                        cookies through
                        your browser settings. Disabling cookies may affect core functionality like sign‑in or bookings.
                    </p>
                </Section>

                <Section id="analytics">
                    <h2>6. Analytics</h2>
                    <p>
                        We may use analytics tools to understand usage and improve the Site. Our configuration allows
                        connections
                        to PostHog (a product analytics platform). Analytics may collect pseudonymous usage information
                        such as
                        pages viewed, actions taken, and basic device data. Depending on your location, analytics may
                        rely on our
                        legitimate interests and/or your consent, particularly where cookies are used.
                    </p>
                </Section>

                <Section id="payments">
                    <h2>7. Payments</h2>
                    <p>
                        We use Stripe to process payments. When you purchase a class pass or complete checkout, certain
                        information
                        is shared with Stripe (such as your email, amount, currency, and order identifiers). Stripe is a
                        separate
                        controller/processor for your payment data and processes it in accordance with its own privacy
                        policy.
                        We do not store full payment card numbers on our servers.
                    </p>
                    <p>
                        For more information, please refer to Stripe's Privacy Policy at stripe.com.
                    </p>
                </Section>

                <Section id="sharing">
                    <h2>8. Disclosure of Information</h2>
                    <p>
                        We share information only as needed to provide the Services, comply with the law, protect our
                        rights,
                        or with your consent. This includes:
                    </p>
                    <ul>
                        <li>Service Providers and Partners: e.g., Stripe for payments; analytics providers (e.g.,
                            PostHog) where enabled.
                        </li>
                        <li>Legal and Compliance: when required by applicable law or to respond to valid legal
                            requests.
                        </li>
                        <li>Business Transfers: in connection with a reorganisation or similar event, consistent with
                            this Policy.
                        </li>
                    </ul>
                </Section>

                <Section id="international">
                    <h2>9. International Transfers</h2>
                    <p>
                        Our service providers may process information outside of the UK. Where applicable, we rely on
                        appropriate
                        safeguards such as standard contractual clauses, adequacy regulations, or other lawful transfer
                        mechanisms.
                    </p>
                </Section>

                <Section id="retention">
                    <h2>10. Data Retention</h2>
                    <p>
                        We retain personal information for as long as necessary to provide the Services and for
                        legitimate and legal
                        purposes (e.g., tax/accounting). Health information, if provided, is retained only as long as
                        needed to support
                        your participation or until you withdraw consent, whichever occurs first, subject to any legal
                        obligations.
                    </p>
                </Section>

                <Section id="security">
                    <h2>11. Security</h2>
                    <p>
                        We take reasonable technical and organisational measures to protect your information. No online
                        service can
                        be completely secure. You are responsible for keeping your account credentials confidential.
                    </p>
                </Section>

                <Section id="your-rights">
                    <h2>12. Your Rights</h2>
                    <p>
                        Depending on your location, you may have rights to access, correct, delete, or port your data,
                        and to object or
                        restrict certain processing. Where processing is based on consent, you can withdraw consent at
                        any time. To
                        exercise your rights, please contact us using the details below.
                    </p>
                </Section>

                <Section id="children">
                    <h2>13. Children</h2>
                    <p>
                        Our classes are for adults. If we learn we have collected personal information from a child
                        without appropriate consent, we will take steps to delete such information.
                    </p>
                </Section>

                <Section id="changes">
                    <h2>14. Changes to this Policy</h2>
                    <p>
                        We may update this Privacy Policy from time to time. The "Last Updated" date at the top
                        indicates the latest
                        revision. Significant changes will be communicated via the Site. Your continued use after
                        changes indicates
                        acceptance of the updated Policy.
                    </p>
                </Section>

                <Section id="contact">
                    <h2>15. Contact</h2>
                    <p>
                        If you have questions about this Privacy Policy or how we handle your data, please contact us
                        via the details
                        provided on our Site.
                    </p>
                </Section>
            </Container>
        </Page>
    );
}